import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { googleAdsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  IndianRupee, MousePointer, Users, CalendarCheck, TrendingUp, TrendingDown,
  Loader2, RefreshCw, AlertTriangle, Info, MapPin, Package, Layers, Search,
  Smartphone, ChevronDown, ChevronRight, Clock, Target, Wallet, Calendar, Filter,
  Lightbulb, CheckCircle2, AlertCircle, ArrowUpRight, ArrowDownRight, Gauge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  AdsProgressBar, AdsUpdatingPill, AdsAnalyticsSkeleton,
} from '@/components/ads/AdsLoadingUI';
import { UnservicedDemandCard } from '@/components/ads/UnservicedDemandCard';
import { DemandMapCard } from '@/components/ads/DemandMapCard';
import { ConversionDeliveryCard } from '@/components/ads/ConversionDeliveryCard';

const inr = (v) => `₹${Math.round(Number(v || 0)).toLocaleString('en-IN')}`;
const num = (v) => Number(v || 0).toLocaleString('en-IN');
const pct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);

const istToday = () => {
  const now = new Date();
  return new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
};
const iso = (d) => d.toISOString().slice(0, 10);
const rangeFor = (days) => {
  const to = istToday();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  return { from: iso(from), to: iso(to) };
};
const monthToDate = () => {
  const to = istToday();
  return { from: iso(new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), 1))), to: iso(to) };
};

// Same shelf of presets as Ads Management > Inspections, so the two tabs behave
// identically. "This Month" is the calendar month here, because that is what a month is.
const DATE_PRESETS = [
  { key: 'today', label: 'Today', range: () => rangeFor(1) },
  { key: 'week', label: 'This Week', range: () => rangeFor(7) },
  { key: 'month', label: 'This Month', range: monthToDate },
  { key: '30days', label: 'Last 30 Days', range: () => rangeFor(30) },
  { key: '60days', label: 'Last 60 Days', range: () => rangeFor(60) },
  { key: '90days', label: 'Last 90 Days', range: () => rangeFor(90) },
];

const SORT_OPTIONS = [
  { value: 'spend', label: 'Ad Spend' },
  { value: 'leads', label: 'Leads' },
  { value: 'payers', label: 'Paying customers' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'roi_pct', label: 'ROI' },
  { value: 'cpl', label: 'Cost per lead' },
  { value: 'cost_per_paid', label: 'Cost per customer' },
  { value: 'clicks', label: 'Clicks' },
];

const VERDICT_OPTIONS = ['SCALE', 'HOLD', 'FIX', 'PAUSE', 'TOO EARLY', 'NO SPEND'];


const RoiCell = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400" title="No spend recorded for this row">—</span>;
  }
  const good = value >= 0;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${good ? 'text-emerald-600' : 'text-red-600'}`}>
      {good ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {value.toFixed(0)}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'slate', testId, delta }) => (
  <div className="bg-white rounded-xl border p-4 transition-shadow hover:shadow-sm" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <Icon className={`h-4 w-4 text-${tone}-400`} />
    </div>
    <div className="mt-2 flex items-baseline gap-2 flex-wrap">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {delta && <Delta {...delta} />}
    </div>
    {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
  </div>
);

// "+18%" against the previous window of the same length. `goodWhenUp = null` keeps it
// neutral (spend going up is neither good nor bad on its own).
const Delta = ({ value, goodWhenUp = true, testId }) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const up = value >= 0;
  const tone = goodWhenUp === null ? 'text-gray-500 bg-gray-100'
    : (up === goodWhenUp ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50');
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-md ${tone}`}
          title="vs the previous window of the same length" data-testid={testId}>
      <Arrow className="h-3 w-3" />{Math.abs(value).toFixed(0)}%
    </span>
  );
};

const VERDICTS = {
  SCALE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  HOLD: 'bg-amber-50 text-amber-700 border-amber-200',
  FIX: 'bg-orange-50 text-orange-700 border-orange-200',
  PAUSE: 'bg-red-50 text-red-700 border-red-200',
  'TOO EARLY': 'bg-slate-50 text-slate-500 border-slate-200',
  'NO SPEND': 'bg-slate-50 text-slate-400 border-slate-200',
};

const VerdictBadge = ({ verdict, reason, testId }) => {
  if (!verdict) return <span className="text-gray-300">—</span>;
  return (
    <span className={`inline-block text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border ${VERDICTS[verdict] || VERDICTS['NO SPEND']}`}
          title={reason} data-testid={testId}>
      {verdict}
    </span>
  );
};

const SEVERITY = {
  bad: { icon: AlertTriangle, box: 'border-l-red-500 bg-red-50/60', tint: 'text-red-600', tag: 'Act now' },
  warn: { icon: AlertCircle, box: 'border-l-amber-500 bg-amber-50/60', tint: 'text-amber-600', tag: 'Watch' },
  good: { icon: CheckCircle2, box: 'border-l-emerald-500 bg-emerald-50/60', tint: 'text-emerald-600', tag: 'Working' },
  info: { icon: Info, box: 'border-l-slate-400 bg-slate-50/60', tint: 'text-slate-500', tag: 'Context' },
};

const InsightCard = ({ item }) => {
  const s = SEVERITY[item.severity] || SEVERITY.info;
  const Icon = s.icon;
  return (
    <div className={`border border-l-4 rounded-lg p-4 ${s.box} transition-transform hover:-translate-y-0.5`}
         data-testid={`insight-${item.id}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${s.tint}`} />
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-gray-900">{item.title}</h4>
            <span className={`text-[10px] uppercase font-bold tracking-wider ${s.tint}`}>{s.tag}</span>
          </div>
          <p className="text-sm text-gray-700">{item.detail}</p>
          <p className="text-xs text-gray-600"><span className="font-semibold">Do this: </span>{item.action}</p>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, subtitle, right, children, testId, collapsible = false,
                   defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border mb-6 overflow-hidden" data-testid={testId}>
      <div
        className={`px-5 py-4 border-b flex items-center justify-between gap-3 ${collapsible ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={collapsible ? () => setOpen(!open) : undefined}
        data-testid={collapsible ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-2 min-w-0">
          {collapsible && (open
            ? <ChevronDown className="h-4 w-4 text-gray-400" />
            : <ChevronRight className="h-4 w-4 text-gray-400" />)}
          {Icon && <Icon className="h-4 w-4 text-gray-500" />}
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {open && children}
    </div>
  );
};

const Th = ({ children, right }) => (
  <th className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500 ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);
const Td = ({ children, right, className = '' }) => (
  <td className={`px-4 py-2.5 text-sm ${right ? 'text-right' : ''} ${className}`}>{children}</td>
);

const FUNNEL_TONES = ['bg-sky-400', 'bg-blue-500', 'bg-indigo-500', 'bg-violet-500',
  'bg-fuchsia-500', 'bg-emerald-500', 'bg-teal-500'];

const stepTone = (v) => (v >= 60 ? 'text-emerald-600' : v >= 25 ? 'text-amber-600' : 'text-red-600');

/** A funnel that tapers, names what it costs at each step, and says where the leads go.
 *  Bars scale against the WIDEST stage (paid clicks), so the shape is the story. */
const Funnel = ({ rows, clickToLead }) => {
  const widest = Math.max(...rows.map((r) => Number(r.count) || 0), 1);
  return (
    <div className="p-5">
      <div className="space-y-1">
        {rows.map((s, i) => {
          const count = Number(s.count) || 0;
          const w = count > 0 ? Math.max(1.5, count / widest * 100) : 0;
          const inside = w >= 20;
          const next = rows[i + 1];
          return (
            <div key={s.stage}
                 data-testid={`funnel-${s.stage.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-4">
                <div className="w-44 shrink-0">
                  <p className="text-sm font-semibold text-gray-800">{s.stage}</p>
                  <p className="text-[11px] text-gray-400">
                    {s.cost_each ? `${inr(s.cost_each)} each` : (i === 0 ? 'bought from Google' : '—')}
                  </p>
                </div>
                <div className="relative flex-1 h-10 rounded-lg bg-gray-100/80 overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 ${FUNNEL_TONES[i % FUNNEL_TONES.length]} rounded-lg transition-[width] duration-700 ease-out`}
                       style={{ width: `${w}%` }} />
                  <span className={`absolute top-1/2 -translate-y-1/2 text-sm font-bold tabular-nums ${inside ? 'left-3 text-white' : 'text-gray-800'}`}
                        style={inside ? undefined : { left: `calc(${w}% + 10px)` }}>
                    {num(s.count)}
                  </span>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500">
                    {i === 0 ? 'paid clicks' : `${pct(s.pct_of_leads)} of leads`}
                  </span>
                </div>
                <div className="w-32 shrink-0 text-right">
                  {i === 0 ? (
                    <p className="text-[11px] text-gray-400">start of funnel</p>
                  ) : s.step_pct === null || s.step_pct === undefined ? (
                    <p className="text-[11px] text-gray-400">—</p>
                  ) : (
                    <>
                      <p className={`text-sm font-bold ${stepTone(s.step_pct)}`}>{pct(s.step_pct)}</p>
                      <p className="text-[11px] text-gray-400">of {rows[i - 1].stage.toLowerCase()}</p>
                    </>
                  )}
                </div>
              </div>
              {next && next.lost > 0 && (
                <div className="flex items-center gap-4">
                  <div className="w-44 shrink-0" />
                  <div className="flex-1 flex items-center gap-1.5 py-1 text-[11px] text-gray-400">
                    <ArrowDownRight className="h-3 w-3 shrink-0" />
                    <span><strong className="text-gray-600">{num(next.lost)}</strong> lost before {next.stage.toLowerCase()}</span>
                  </div>
                  <div className="w-32 shrink-0" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {clickToLead !== null && clickToLead !== undefined && (
        <p className="mt-4 pt-3 border-t text-xs text-gray-500">
          {pct(clickToLead)} of paid clicks became a service lead. Everything below "Leads"
          is the CRM's own work — an extra rupee of sales effort beats an extra rupee of ad
          spend wherever the step conversion is red.
        </p>
      )}
    </div>
  );
};

export default function ServicesAdAnalytics() {
  const { user } = useAuth();
  const canSync = ['SUPER_ADMIN', 'CEO', 'CTO', 'ADMIN', 'COUNTRY_HEAD'].includes(user?.role_code);
  const [preset, setPreset] = useState('30days');
  const [range, setRange] = useState(rangeFor(30));
  const [customDraft, setCustomDraft] = useState(rangeFor(30));
  const [metroRollup, setMetroRollup] = useState(false);
  const [basis, setBasis] = useState('received');
  const [cityFilter, setCityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [sortBy, setSortBy] = useState('spend');
  const [sortOrder, setSortOrder] = useState('desc');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [health, setHealth] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await googleAdsApi.getPerformance({
        date_from: range.from, date_to: range.to, metro_rollup: metroRollup, basis,
        ...(cityFilter !== 'all' ? { city: cityFilter } : {}),
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Google Ads performance');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, metroRollup, basis, cityFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    googleAdsApi.getSyncStatus().then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  const refreshHealth = () => googleAdsApi.getSyncStatus()
    .then((r) => setHealth(r.data)).catch(() => {});

  // The sync runs in the background now (a 30-day pull outlives the 120s proxy timeout),
  // so the button watches the job instead of waiting on the request.
  const watchJob = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await googleAdsApi.getSyncJob();
        const job = r.data?.job || null;
        setSyncJob(job);
        if (!job || job.status === 'running') return;
        clearInterval(pollRef.current);
        setSyncing(false);
        if (job.status === 'done') {
          const rows = Object.values(job.stats?.written || {}).reduce((a, b) => a + b, 0);
          toast.success(`Synced ${num(rows)} spend rows and ${num(job.clicks?.gclids_stored || 0)} clicks`);
          load();
          refreshHealth();
        } else {
          toast.error(job.error || 'Sync failed');
        }
      } catch {
        /* keep polling — a blip should not kill the watcher */
      }
    }, 3000);
  }, [load]);

  useEffect(() => {
    // A sync started before a reload (or by someone else) still gets watched.
    googleAdsApi.getSyncJob().then((r) => {
      if (r.data?.job?.status === 'running') {
        setSyncJob(r.data.job);
        setSyncing(true);
        watchJob();
      }
    }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [watchJob]);

  const applyPreset = (p) => { setPreset(p.key); setRange(p.range()); };

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await googleAdsApi.sync({ date_from: range.from, date_to: range.to });
      setSyncJob(res.data?.job || null);
      toast.info(res.data?.status === 'already_running'
        ? 'A sync is already running — watching it'
        : `Syncing ${range.from} → ${range.to} in the background`);
      watchJob();
    } catch (e) {
      setSyncing(false);
      toast.error(e?.response?.data?.detail || 'Could not start the sync');
    }
  };

  const t = data?.totals || {};
  const prev = data?.previous || {};
  const attr = data?.attribution || {};
  const insights = data?.insights || [];
  const bench = data?.benchmarks || {};
  const leak = data?.spend_no_leads || [];
  const leakTotal = useMemo(() => leak.reduce((s, r) => s + Number(r.spend || 0), 0), [leak]);
  const deltaPct = (now, before) => (Number(before)
    ? ((Number(now || 0) - Number(before)) / Number(before)) * 100 : null);

  const hit = useCallback((...fields) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return fields.some((f) => String(f || '').toLowerCase().includes(q));
  }, [search]);

  const sorted = useCallback((rows) => {
    const dir = sortOrder === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const x = a[sortBy], y = b[sortBy];
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;     // blanks last, both directions
      if (y === null || y === undefined) return -1;
      return (Number(x) - Number(y)) * dir;
    });
  }, [sortBy, sortOrder]);

  const campaignRows = useMemo(() => sorted((data?.by_campaign || []).filter((r) => (
    hit(r.campaign_name, r.channel_type)
    && (stateFilter === 'all' || (r.campaign_status || '').toUpperCase() === stateFilter)
    && (verdictFilter === 'all' || r.verdict === verdictFilter)
  ))), [data?.by_campaign, hit, stateFilter, verdictFilter, sorted]);

  const cityRows = useMemo(() => sorted((data?.by_city || []).filter((r) => (
    hit(r.city) && (verdictFilter === 'all' || r.verdict === verdictFilter)
  ))), [data?.by_city, hit, verdictFilter, sorted]);

  const packageRows = useMemo(() => sorted((data?.by_package || []).filter((r) => hit(r.package))),
    [data?.by_package, hit, sorted]);
  const assetRows = useMemo(() => sorted((data?.by_asset_group || [])
    .filter((r) => hit(r.entity_name, r.campaign_name))), [data?.by_asset_group, hit, sorted]);
  const keywordRows = useMemo(() => sorted((data?.by_keyword || [])
    .filter((r) => hit(r.entity_name, r.campaign_name, r.match_type))),
    [data?.by_keyword, hit, sorted]);
  const filtersOn = search.trim() || stateFilter !== 'all' || verdictFilter !== 'all';
  const funnelRows = data?.funnel || [];
  const clickToLead = funnelRows.length > 1 && funnelRows[0].count
    ? Number((funnelRows[1].count / funnelRows[0].count * 100).toFixed(1)) : null;


  if (loading && !data) {
    return <AdsAnalyticsSkeleton label="Loading Google Ads performance…" />;
  }

  return (
    <div data-testid="services-ads-analytics">
      <AdsProgressBar active={loading} />
      <AdsUpdatingPill
        active={loading}
        label="Fetching Google Ads for the new range…"
        sub={`${range.from} → ${range.to}`}
      />
      {/* date range — same shelf of presets as the Inspections tab */}
      <div className="bg-white rounded-xl border p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-600">Date Range:</span>
          <div className="flex flex-wrap items-center gap-2">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  preset === p.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`services-ads-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() => { setCustomDraft(range); setPreset('custom'); }}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                preset === 'custom' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid="services-ads-preset-custom"
            >
              Custom
            </button>
          </div>
          <div className="flex-1" />
          {loading ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-blue-600"
                  data-testid="services-ads-window-loading">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading {range.from} → {range.to}…
            </span>
          ) : (
            <span className="text-xs text-gray-400" data-testid="services-ads-window-label">
              {range.from} → {range.to}
            </span>
          )}
        </div>

        {preset === 'custom' && (
          <div className="mt-3 pt-3 border-t flex flex-wrap items-end gap-3" data-testid="services-ads-custom-range">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input type="date" value={customDraft.from} max={iso(istToday())}
                     onChange={(e) => setCustomDraft((d) => ({ ...d, from: e.target.value }))}
                     className="border rounded-lg px-3 py-1.5 text-sm text-gray-800"
                     data-testid="services-ads-date-from" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input type="date" value={customDraft.to} max={iso(istToday())}
                     onChange={(e) => setCustomDraft((d) => ({ ...d, to: e.target.value }))}
                     className="border rounded-lg px-3 py-1.5 text-sm text-gray-800"
                     data-testid="services-ads-date-to" />
            </div>
            <Button size="sm" data-testid="services-ads-date-apply"
                    disabled={!customDraft.from || !customDraft.to || customDraft.from > customDraft.to}
                    onClick={() => setRange({ from: customDraft.from, to: customDraft.to })}>
              Apply
            </Button>
            {customDraft.from > customDraft.to && (
              <span className="text-xs font-medium text-red-600" data-testid="services-ads-date-error">
                &ldquo;From&rdquo; must be on or before &ldquo;To&rdquo;
              </span>
            )}
          </div>
        )}
      </div>

      {/* search + filters + sort */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[220px] max-w-md">
          <Input placeholder="Search campaign, city, package, keyword…" value={search}
                 onChange={(e) => setSearch(e.target.value)} className="h-10"
                 data-testid="services-ads-search" />
        </div>

        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="w-44 h-10" data-testid="services-ads-city-filter">
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {(data?.city_options || []).map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-40 h-10" data-testid="services-ads-state-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="ENABLED">Enabled</SelectItem>
            <SelectItem value="PAUSED">Paused</SelectItem>
            <SelectItem value="REMOVED">Removed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={verdictFilter} onValueChange={setVerdictFilter}>
          <SelectTrigger className="w-40 h-10" data-testid="services-ads-verdict-filter">
            <SelectValue placeholder="All Verdicts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Verdicts</SelectItem>
            {VERDICT_OPTIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 h-10" data-testid="services-ads-sort-by">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="px-2"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  data-testid="services-ads-sort-order"
                  title={sortOrder === 'desc' ? 'Highest first' : 'Lowest first'}>
            {sortOrder === 'desc' ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* how the money is counted + sync */}
      <div className="bg-white rounded-xl border p-3 mb-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setMetroRollup(!metroRollup)}
          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            metroRollup ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
          data-testid="services-ads-metro-toggle"
          title="Fold suburbs into their metro (Secunderabad → Hyderabad, Thane → Mumbai)"
        >
          <MapPin className="h-3.5 w-3.5 inline mr-1.5" />
          {metroRollup ? 'Metro roll-up: ON' : 'Metro roll-up: OFF'}
        </button>
        <button
          onClick={() => setBasis(basis === 'received' ? 'cohort' : 'received')}
          className="px-3 py-1.5 text-sm rounded-lg border bg-white text-gray-600 hover:bg-gray-50 transition-colors"
          data-testid="services-ads-basis-toggle"
          title="Received = cash that landed in this window (CEO rule). Cohort = money ever collected by leads created in this window."
        >
          <IndianRupee className="h-3.5 w-3.5 inline mr-1.5" />
          {basis === 'received' ? 'Money: cash received' : 'Money: lead cohort'}
        </button>
        {cityFilter !== 'all' && (
          <span className="text-xs px-2 py-1 rounded-md border bg-blue-50 text-blue-700 border-blue-200"
                data-testid="services-ads-city-badge" title={data?.notes?.city_filter}>
            <MapPin className="h-3 w-3 inline mr-1" />{cityFilter} only
          </span>
        )}
        <div className="flex-1" />
        {data?.spend_source && (
          <span
            className={`text-xs px-2 py-1 rounded-md border ${
              data.spend_source === 'google_ads_api' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : data.spend_source === 'mixed' ? 'bg-sky-50 text-sky-700 border-sky-200'
                  : data.spend_source === 'google_ads_script' ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-red-50 text-red-700 border-red-200'}`}
            data-testid="services-ads-spend-source"
            title={`${data.spend_coverage?.days_from_api ?? 0} of ${data.spend_coverage?.days_in_window ?? 0} days from the Google Ads API, ${data.spend_coverage?.days_from_script ?? 0} from the hourly Ads Script`}
          >
            {data.spend_source === 'google_ads_api' ? 'Spend: Google Ads API'
              : data.spend_source === 'mixed' ? `Spend: API ${data.spend_coverage?.days_from_api}d + Script ${data.spend_coverage?.days_from_script}d`
                : data.spend_source === 'google_ads_script' ? 'Spend: Ads Script (campaign only)'
                  : 'Spend unavailable'}
          </span>
        )}
        {data?.last_synced && (
          <span className="text-xs text-gray-400 flex items-center gap-1" data-testid="services-ads-last-synced">
            <Clock className="h-3 w-3" /> {new Date(data.last_synced).toLocaleString('en-IN')}
          </span>
        )}
        {canSync && (
          <Button size="sm" variant="outline" onClick={runSync} disabled={syncing}
                  data-testid="services-ads-sync-btn"
                  title="Pulls Google's numbers for the selected range in the background">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">
              {syncing
                ? `Syncing… ${syncJob?.phase || 'starting'}${syncJob?.steps ? ` (${(syncJob.step || 0) + 1}/${syncJob.steps})` : ''}`
                : 'Sync this range'}
            </span>
          </Button>
        )}
      </div>

      {/* Everything below still shows the PREVIOUS window until the new fetch lands, so
          dim it (and lock it) rather than unmount it — a stale figure must never be
          mistaken for the new one, and the layout must not jump under the cursor. */}
      <div
        className={`transition-opacity duration-200 ${loading ? 'pointer-events-none select-none' : ''}`}
        style={{ opacity: loading ? 0.4 : 1 }}
        aria-busy={loading}
        data-testid="services-ads-results"
      >
      {!data?.configured && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-800"
             data-testid="services-ads-not-configured">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          Google Ads API is not configured — missing {(data?.missing_env || []).join(', ')}.
        </div>
      )}

      {health?.click_history_at_risk && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-900"
             data-testid="services-ads-click-horizon-warning">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          Google only keeps click data for 90 days (before {health.google_click_horizon} is gone
          for good). We hold {num(health.gclids_stored)} clicks across {health.click_days_stored} days
          ({health.click_days_oldest} → {health.click_days_newest}).
        </div>
      )}

      {data && !data.click_coverage?.covered && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-900"
             data-testid="services-ads-coverage-warning">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          Click history is missing for {data.click_coverage.days_in_window - data.click_coverage.days_snapshotted}
          {' '}of {data.click_coverage.days_in_window} days in this range
          {data.click_coverage.missing_days?.length > 0 && (
            <> ({data.click_coverage.missing_days.slice(0, 5).join(', ')}
              {data.click_coverage.missing_days.length > 5 ? '…' : ''})</>
          )}, so {num(attr.unresolved_leads)} Google leads cannot be tied to a campaign yet
          and campaign rows are understated. Google keeps clicks for 90 days only —
          {canSync ? ' run Sync to snapshot what is still there.' : ' ask an admin to run Sync.'}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Wallet} label="Google spend" value={inr(t.spend)}
             delta={{ value: deltaPct(t.spend, prev.spend), goodWhenUp: null, testId: 'delta-spend' }}
             sub={`${num(t.clicks)} clicks · ${inr(t.cpc || 0)}/click`} testId="kpi-spend" />
        <Kpi icon={Users} label="Leads" value={num(t.leads)}
             delta={{ value: deltaPct(t.leads, prev.leads), testId: 'delta-leads' }}
             sub={t.cpl ? `${inr(t.cpl)} per lead` : '—'} testId="kpi-leads" />
        <Kpi icon={CalendarCheck} label="Booked" value={num(t.booked)}
             sub={`${pct(t.lead_to_payer_pct)} of leads paid`} testId="kpi-booked" />
        <Kpi icon={IndianRupee} label={basis === 'received' ? 'Revenue received' : 'Revenue (lead cohort)'}
             value={inr(t.revenue)}
             delta={{ value: deltaPct(t.revenue, prev.revenue), testId: 'delta-revenue' }}
             sub={`${num(t.payers)} paying customers`} testId="kpi-revenue" />
        <Kpi icon={Target} label="ROI" value={t.roi_pct === null || t.roi_pct === undefined ? '—' : `${Number(t.roi_pct).toFixed(0)}%`}
             sub={prev.roas ? `ROAS ${Number(t.roas || 0).toFixed(2)} vs ${Number(prev.roas).toFixed(2)} last period` : 'revenue vs spend, same window'}
             testId="kpi-roi" />
        <Kpi icon={MousePointer} label="Cost per paying customer"
             value={t.cost_per_paid ? inr(t.cost_per_paid) : '—'}
             delta={{ value: deltaPct(t.cost_per_paid, prev.cost_per_paid), goodWhenUp: false, testId: 'delta-cac' }}
             sub={t.aov ? `${inr(t.aov)} average order` : '—'} testId="kpi-cac" />
        <Kpi icon={IndianRupee} label="All-source service revenue" value={inr(t.revenue_all_sources)}
             sub={`${num(t.payers_all_sources)} payers incl. non-Google`} testId="kpi-revenue-all" />
        <Kpi icon={AlertTriangle} label="Spend with no leads" value={inr(leakTotal)}
             sub={`${leak.length} campaign/city rows`} testId="kpi-leak" />
      </div>

      {/* Is Google actually receiving what we earned? A stalled sweep hid ₹255k
          for 15 days because this number was never on screen. */}
      <ConversionDeliveryCard />

      {/* what a performance marketer would say about the numbers above */}
      {insights.length > 0 && (
        <Section title="What the numbers say" icon={Lightbulb}
                 subtitle={`Read against this window's own averages — CPL ${bench.avg_cpl ? inr(bench.avg_cpl) : '—'}, cost per customer ${bench.avg_cost_per_paid ? inr(bench.avg_cost_per_paid) : '—'}, close rate ${pct(bench.avg_close_rate_pct)}, target ROAS ${bench.target_roas}×`}
                 right={<span className="text-xs text-gray-400 whitespace-nowrap">
                   {bench.campaigns_judged} of {bench.campaigns_total} rows have enough spend to judge
                 </span>}
                 testId="services-ads-insights">
          <div className="p-5 grid gap-3 lg:grid-cols-2">
            {insights.map((i) => <InsightCard key={i.id} item={i} />)}
          </div>
        </Section>
      )}

      {/* reconciliation */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-sm text-slate-700"
           data-testid="services-ads-reconciliation">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
          <div className="space-y-1">
            <p>
              <strong>{num(attr.cohort_leads)}</strong> service leads came in during this window:
              <strong> {num(attr.google_leads)}</strong> from a Google click
              (<strong>{num(attr.resolved_leads)}</strong> tied to a campaign,
              {' '}<strong>{num(attr.privacy_leads)}</strong> with the campaign withheld by Google on iOS,
              {' '}<strong>{num(attr.unresolved_leads)}</strong> whose click is older than Google's
              90-day history) and <strong>{num(attr.non_google_leads)}</strong> from website, WhatsApp
              or the CRM directly.
            </p>
            <p>
              Cash collected in this window: <strong>{inr(attr.revenue_all_sources)}</strong> total —
              <strong> {inr(attr.google_revenue)}</strong> on Google leads,
              <strong> {inr(attr.non_google_revenue)}</strong> on everything else.
              {Number(attr.unresolved_revenue) > 0 && (
                <> {inr(attr.unresolved_revenue)} of the Google money sits in the unresolved bucket.</>
              )}
            </p>
            <p className="text-xs text-slate-500">{data?.notes?.basis}</p>
            <p className="text-xs text-slate-500">{data?.notes?.cost_per_paid}</p>
          </div>
        </div>
      </div>

      {leak.length > 0 && (
        <Section title="Spend that produced no leads" icon={AlertTriangle}
                 subtitle="Every rupee here bought clicks that never became a service lead"
                 testId="services-ads-leak-section">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-red-50">
                <tr><Th>Scope</Th><Th>Name</Th><Th right>Clicks</Th><Th right>Spend</Th></tr>
              </thead>
              <tbody className="divide-y">
                {leak.map((r, i) => (
                  <tr key={`${r.scope}-${r.name}-${i}`} data-testid={`leak-row-${i}`}>
                    <Td><span className="text-xs uppercase text-gray-500">{r.scope}</span></Td>
                    <Td className="font-medium">{r.name}</Td>
                    <Td right>{num(r.clicks)}</Td>
                    <Td right className="font-semibold text-red-600">{inr(r.spend)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* funnel */}
      <Section title="Google lead funnel" icon={Gauge}
               subtitle="Leads created in this window, tracked to service completion — with what each step costs"
               right={
                 <div className="hidden md:flex items-center gap-5 text-right">
                   <div>
                     <p className="text-[11px] uppercase tracking-wide text-gray-400">Click → lead</p>
                     <p className="text-sm font-bold text-gray-800">{pct(clickToLead)}</p>
                   </div>
                   <div>
                     <p className="text-[11px] uppercase tracking-wide text-gray-400">Lead → paid</p>
                     <p className="text-sm font-bold text-gray-800">{pct(t.lead_to_payer_pct)}</p>
                   </div>
                   <div>
                     <p className="text-[11px] uppercase tracking-wide text-gray-400">Per booking</p>
                     <p className="text-sm font-bold text-gray-800">
                       {funnelRows.find((s) => s.stage === 'Booked')?.cost_each
                         ? inr(funnelRows.find((s) => s.stage === 'Booked').cost_each) : '—'}
                     </p>
                   </div>
                   <div>
                     <p className="text-[11px] uppercase tracking-wide text-gray-400">Per customer</p>
                     <p className="text-sm font-bold text-gray-800">
                       {t.cost_per_paid ? inr(t.cost_per_paid) : '—'}
                     </p>
                   </div>
                 </div>
               }
               testId="services-ads-funnel">
        <Funnel rows={funnelRows} clickToLead={clickToLead} />
      </Section>

      {/* campaigns */}
      <Section title="By campaign" icon={Layers}
               subtitle="Spend from Google, leads and money from the CRM — verdicts use this window's own averages"
               right={filtersOn ? (
                 <span className="text-xs text-gray-400 whitespace-nowrap" data-testid="services-ads-campaign-count">
                   {campaignRows.length} of {(data?.by_campaign || []).length} rows
                 </span>
               ) : null}
               testId="services-ads-campaigns">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <Th>Campaign</Th><Th right>Spend</Th><Th right>Clicks</Th><Th right>Leads</Th>
                <Th right>CPL</Th><Th right>Booked</Th><Th right>Payers</Th><Th right>Revenue</Th>
                <Th right>Cost/customer</Th><Th right>ROI</Th><Th>Verdict</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {campaignRows.map((r) => (
                <tr key={r.campaign_id} className="hover:bg-gray-50"
                    data-testid={`campaign-row-${r.campaign_id}`}>
                  <Td>
                    <div className="font-medium text-gray-900">{r.campaign_name}</div>
                    <div className="text-xs text-gray-400">
                      {r.channel_type || '—'}{r.campaign_status ? ` · ${r.campaign_status}` : ''}
                    </div>
                  </Td>
                  <Td right>{inr(r.spend)}</Td>
                  <Td right>{num(r.clicks)}</Td>
                  <Td right className="font-medium">{num(r.leads)}</Td>
                  <Td right>{r.cpl ? inr(r.cpl) : '—'}</Td>
                  <Td right>{num(r.booked)}</Td>
                  <Td right>{num(r.payers)}</Td>
                  <Td right className="font-semibold">{inr(r.revenue)}</Td>
                  <Td right>{r.cost_per_paid ? inr(r.cost_per_paid) : '—'}</Td>
                  <Td right><RoiCell value={r.roi_pct} /></Td>
                  <Td><VerdictBadge verdict={r.verdict} reason={r.verdict_reason}
                                    testId={`campaign-verdict-${r.campaign_id}`} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* cities */}
      <Section title="By city" icon={MapPin}
               subtitle="Spend = the city Google billed us for. Leads and revenue = the customer's own city."
               testId="services-ads-cities">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <Th>City</Th><Th right>Spend</Th><Th right>Clicks</Th><Th right>Leads</Th>
                <Th right>Booked</Th><Th right>Payers</Th><Th right>Revenue</Th>
                <Th right>Cost/customer</Th><Th right>ROI</Th><Th>Verdict</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {cityRows.map((r) => (
                <tr key={r.city} className="hover:bg-gray-50" data-testid={`city-row-${r.city}`}>
                  <Td className="font-medium">{r.city}</Td>
                  <Td right>{inr(r.spend)}</Td>
                  <Td right>{num(r.clicks)}</Td>
                  <Td right>{num(r.leads)}</Td>
                  <Td right>{num(r.booked)}</Td>
                  <Td right>{num(r.payers)}</Td>
                  <Td right className="font-semibold">{inr(r.revenue)}</Td>
                  <Td right>{r.cost_per_paid ? inr(r.cost_per_paid) : '—'}</Td>
                  <Td right><RoiCell value={r.roi_pct} /></Td>
                  <Td><VerdictBadge verdict={r.verdict} reason={r.verdict_reason}
                                    testId={`city-verdict-${r.city}`} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* where we cannot serve the demand we are already paying for */}
      <UnservicedDemandCard from={range.from} to={range.to} />

      {/* the same demand, on a map — and a dot per customer inside any city */}
      <DemandMapCard from={range.from} to={range.to} />

      {/* packages */}
      <Section title="By package" icon={Package}
               subtitle="Which service the Google customers actually bought"
               testId="services-ads-packages">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <Th>Package</Th><Th right>Leads</Th><Th right>Quoted</Th><Th right>Booked</Th>
                <Th right>Payers</Th><Th right>Close rate</Th><Th right>Revenue</Th><Th right>Avg order</Th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {packageRows.map((r) => (
                <tr key={r.package} className="hover:bg-gray-50" data-testid={`package-row-${r.package}`}>
                  <Td className="font-medium">{r.package}</Td>
                  <Td right>{num(r.leads)}</Td>
                  <Td right>{num(r.quoted)}</Td>
                  <Td right>{num(r.booked)}</Td>
                  <Td right>{num(r.payers)}</Td>
                  <Td right>{pct(r.lead_to_payer_pct)}</Td>
                  <Td right className="font-semibold">{inr(r.revenue)}</Td>
                  <Td right>{r.aov ? inr(r.aov) : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* drill-downs */}
      {(data?.by_asset_group || []).length > 0 && (
        <Section title="By creative set (Performance Max asset groups)" icon={Layers}
                 collapsible defaultOpen={false} testId="services-ads-asset-groups">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr><Th>Asset group</Th><Th>Campaign</Th><Th right>Spend</Th><Th right>Clicks</Th>
                  <Th right>CPC</Th><Th right>Google conv.</Th></tr>
              </thead>
              <tbody className="divide-y">
                {assetRows.map((r) => (
                  <tr key={`${r.campaign_id}-${r.entity_id}`} data-testid={`asset-group-row-${r.entity_id}`}>
                    <Td className="font-medium">{r.entity_name}</Td>
                    <Td className="text-gray-500">{r.campaign_name}</Td>
                    <Td right>{inr(r.spend)}</Td>
                    <Td right>{num(r.clicks)}</Td>
                    <Td right>{r.cpc ? inr(r.cpc) : '—'}</Td>
                    <Td right>{Number(r.google_conversions || 0).toFixed(0)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {(data?.by_keyword || []).length > 0 && (
        <Section title="By keyword (search campaigns)" icon={Search}
                 collapsible defaultOpen={false} testId="services-ads-keywords">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr><Th>Keyword</Th><Th>Match</Th><Th>Campaign</Th><Th right>Spend</Th>
                  <Th right>Clicks</Th><Th right>CPC</Th><Th right>Google conv.</Th></tr>
              </thead>
              <tbody className="divide-y">
                {keywordRows.map((r) => (
                  <tr key={`${r.campaign_id}-${r.entity_id}`} data-testid={`keyword-row-${r.entity_id}`}>
                    <Td className="font-medium">{r.entity_name}</Td>
                    <Td className="text-xs text-gray-500">{r.match_type}</Td>
                    <Td className="text-gray-500">{r.campaign_name}</Td>
                    <Td right>{inr(r.spend)}</Td>
                    <Td right>{num(r.clicks)}</Td>
                    <Td right>{r.cpc ? inr(r.cpc) : '—'}</Td>
                    <Td right>{Number(r.google_conversions || 0).toFixed(0)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {(data?.by_device || []).length > 0 && (
        <Section title="By device" icon={Smartphone} collapsible defaultOpen={false}
                 testId="services-ads-devices">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr><Th>Device</Th><Th right>Spend</Th><Th right>Clicks</Th><Th right>CPC</Th>
                  <Th right>Google conv.</Th></tr>
              </thead>
              <tbody className="divide-y">
                {data.by_device.map((r) => (
                  <tr key={r.device} data-testid={`device-row-${r.device}`}>
                    <Td className="font-medium">{r.device}</Td>
                    <Td right>{inr(r.spend)}</Td>
                    <Td right>{num(r.clicks)}</Td>
                    <Td right>{r.cpc ? inr(r.cpc) : '—'}</Td>
                    <Td right>{Number(r.google_conversions || 0).toFixed(0)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* daily */}
      {(data?.daily || []).length > 1 && (
        <Section title="Spend vs revenue, day by day" icon={TrendingUp} testId="services-ads-daily">
          <div className="p-5" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis yAxisId="money" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="leads" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => (n === 'Leads' ? num(v) : inr(v))} />
                <Legend />
                <Bar yAxisId="money" dataKey="spend" name="Spend" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="money" dataKey="revenue" name="Revenue" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Line yAxisId="leads" type="monotone" dataKey="leads" name="Leads" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}
      </div>
    </div>
  );
}
